import { Component, Input, OnInit, OnDestroy, inject, EventEmitter, Output } from '@angular/core';
import { Session } from '../../entity/entities';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { NotificationClient } from 'src/app/client/notification.client';
import { MessageService } from 'primeng/api';
import { NotificationInstance, NotificationTypeFilter, ReceivedNotification } from '../../entity/notification/notification.model';
import { DateToUTCPipe } from '../../pipes/dateToUTC.pipe';
import { NotificationStatuses } from '../create-notification/create-notification/create-notification.component';
import { environment } from 'src/environments/environment';
import { TlmClient } from 'src/app/client/tlm.client';
import { TlmPermissionHelper } from '../../helpers/tlmPermission.helper';
import { PermissionsCatalogueEnum } from '../../entity/enum/permissionsCatalogue';

const TLM_ALARM_TYPE = 'uclock_alarm';
const TLM_REQUEST_TYPE = 'uclock_request';

@Component({
  selector: 'app-received-notifications-overview',
  templateUrl: './received-notifications-overview.component.html',
  styleUrls: ['./received-notifications-overview.component.scss'],
  animations: [
    trigger('slideDown', [
      state('collapsed', style({
        height: '0',
        overflow: 'hidden',
        opacity: '0'
      })),
      state('expanded', style({
        height: '*',
        overflow: 'visible',
        opacity: '1'
      })),
      transition('collapsed <=> expanded', animate('300ms ease-in-out'))
    ])
  ]
})
export class ReceivedNotificationsOverviewComponent implements OnInit, OnDestroy {

  private notificationClient = inject(NotificationClient);
  private messageService = inject(MessageService);
  private dateToUTCPipe = inject(DateToUTCPipe);
  private tlmClient = inject(TlmClient);

  @Output() viewDetailsRequested = new EventEmitter<ReceivedNotification>();
  @Output() viewConversation = new EventEmitter<any>();
  @Input() activeNotificationId: number | null = null;
  @Input() receivedNotifications: boolean = true;

  groupedNotificationKeys: string[] = [];
  notifications: ReceivedNotification[] = [];
  allNotifications: ReceivedNotification[] = [];
  groupedNotifications: { [key: string]: ReceivedNotification[] } = {};
  pinnedNotifications: ReceivedNotification[] = [];
  /** The alarms among them — for "Acknowledge all". */
  tlmAlarms: ReceivedNotification[] = [];
  isAcknowledgingAll = false;
  notificationTypeFilters: NotificationTypeFilter[] = [];
  selectedFilters: NotificationTypeFilter[] = []; // NEW: for p-multiSelect
  showAddNotificationDialog: boolean = false;
  showUnreadOnly: boolean = false;
  unreadCount: number = 0;
  maxItemMessageLength: number = 150;
  isPdp:boolean = environment.portalCode === 'PDP';

  private destroy$ = new Subject<void>();

  // Define field display order configuration
  private readonly fieldDisplayOrder = [
    ['expire_on', 'created_on'],
    ['status'],
    ['location'],
    ['createdByFullName','created_by' ],
    ['conversation', 'link']
  ];

  ngOnInit(): void {
    this.loadNotificationsFromSession();
    this.subscribeToNotificationUpdates();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadNotificationsFromSession(): void {
    const source = this.receivedNotifications ? Session.userNotifications : Session.userCreatedNotifications;

    this.convertApiNotificationsToDisplay(source.sort((a, b) => {
      const byPriority = ((a as any).Priority ?? 2) - ((b as any).Priority ?? 2);
      if (byPriority !== 0) return byPriority;
      const aDate = new Date(a.PublishedAt ?? a.CreatedAt).getTime();
      const bDate = new Date(b.PublishedAt ?? b.CreatedAt).getTime();
      return bDate - aDate;
    }));
    this.buildNotificationTypeFilters();
    this.selectedFilters = [...this.notificationTypeFilters];
    this.applyFilters();
  }

  subscribeToNotificationUpdates(): void {
    Session.notificationsUpdated$
      ?.pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.loadNotificationsFromSession();
      });
  }


  onViewConversationClick(notificationId: any): void {
    this.viewConversation.emit(notificationId);
  }

  onViewDetailsClick(notification: ReceivedNotification): void {
    if (this.receivedNotifications && !notification.isRead) {
      this.toggleReadStatus(notification);
    }
    this.viewDetailsRequested.emit(notification);
  }

  convertApiNotificationsToDisplay(notificationInstances: any[]): void {
    let idCounter = 1;
    this.allNotifications = notificationInstances.map(instance => {
      return this.convertSingleNotificationToDisplay(instance, idCounter++);
    });
  }

  buildNotificationTypeFilters(): void {
    const uniqueTypes = new Map<string, NotificationTypeFilter>();

    this.allNotifications.forEach(notification => {
      const code = notification.notificationType || '';
      if (code && !uniqueTypes.has(code)) {
        uniqueTypes.set(code, {
          code: code,
          name: notification.notificationInstance?.Name || code,
          iconClass: notification.iconClass || 'pi-bell',
          iconBackgroundColor: notification.iconBackgroundColor || '#ADD8E6',
          selected: true
        });
      }
    });

    this.notificationTypeFilters = Array.from(uniqueTypes.values());
  }

  onFilterItemClick(event: Event, filter: NotificationTypeFilter): void {
    console.log('Filter item clicked:', filter);
    event.preventDefault();
    event.stopPropagation();

    // Toggle the filter selection
    filter.selected = event.defaultPrevented ? filter.selected : !filter.selected;

    if (filter.selected) {
      // Add to selectedFilters if not already there
      if (!this.selectedFilters.some(sf => sf.code === filter.code)) {
        this.selectedFilters.push(filter);
      }
    } else {
      // Remove from selectedFilters
      this.selectedFilters = this.selectedFilters.filter(sf => sf.code !== filter.code);
    }

    this.applyFilters();
  }



  onFilterChange(): void {
    // Update the selected property for each filter based on selectedFilters array
    this.notificationTypeFilters.forEach(filter => {
      filter.selected = this.selectedFilters.some(sf => sf.code === filter.code);
    });
    this.applyFilters();
  }

  // Handle individual checkbox toggle within dropdown
  onFilterItemToggle(event: any, filter: NotificationTypeFilter): void {
    if (filter.selected) {
      // Add to selectedFilters if not already there
      if (!this.selectedFilters.some(sf => sf.code === filter.code)) {
        this.selectedFilters.push(filter);
      }
    } else {
      // Remove from selectedFilters
      this.selectedFilters = this.selectedFilters.filter(sf => sf.code !== filter.code);
    }
    this.applyFilters();
  }


  onShowUnreadToggle(): void {
    this.applyFilters();
  }

  applyFilters(): void {
    const selectedFilters = this.notificationTypeFilters
      .filter(f => f.selected)
      .map(f => f.code);

    this.pinnedNotifications = [];
    this.tlmAlarms = [];

    if (selectedFilters.length === 0) {
      this.notifications = [];
      this.groupedNotifications = {};
      this.groupedNotificationKeys = [];
    } else if (selectedFilters.length === this.notificationTypeFilters.length) {
      this.notifications = [...this.allNotifications];
    } else {
      this.notifications = this.allNotifications.filter(notification =>
        selectedFilters.includes(notification.notificationType || '')
      );
    }

    if (this.receivedNotifications) {
      this.unreadCount = this.allNotifications.filter(n => !n.isRead).length;

      this.pinnedNotifications = this.notifications.filter(n => this.isPinned(n));
      this.tlmAlarms = this.pinnedNotifications.filter(n => n.tlmKind === 'alarm');
      this.notifications = this.notifications.filter(n => !this.isPinned(n));
    }

    if (selectedFilters.length > 0 && this.showUnreadOnly && this.receivedNotifications) {
      this.notifications = this.notifications.filter(n => !n.isRead);
      // TLM items have a read state too (decision 2026-10-20); "Acknowledge all" still covers every alarm.
      this.pinnedNotifications = this.pinnedNotifications.filter(n => !n.isRead);
    }

    if (selectedFilters.length > 0) {
      this.groupNotificationsByDate();
      this.groupedNotificationKeys = Object.keys(this.groupedNotifications);
    }
  }

  convertSingleNotificationToDisplay(instance: any, id: number): ReceivedNotification {
    const findFieldByCode = (code: string) => {
      return instance.Fields?.find(f => f.Code === code);
    };

    const subjectField = findFieldByCode('subject');
    const bodyField = findFieldByCode('body');
    const ticketIdField = findFieldByCode('ticket_id');
    const ticketNumberField = findFieldByCode('ticket_number');

    let createdDate: Date;
    const displayDateSource = instance.PublishedAt ?? instance.CreatedAt;
    if (displayDateSource) {
      createdDate = new Date(displayDateSource);
    }

    let expiresOnDate: Date;
    if (instance.ExpiresAt) {
      expiresOnDate = new Date(instance.ExpiresAt);
    }

    const ticketIdValue = ticketIdField?.Value && ticketIdField.Value.trim() !== ''
      ? ticketIdField.Value
      : null;

    const ticketNumberValue = ticketNumberField?.Value && ticketNumberField.Value.trim() !== ''
      ? ticketNumberField.Value
      : null;

    const notificationType = instance.Code || '';
    const subjectValue = subjectField?.Value || instance.Name || 'Notification';

    let fullTitle: string;

    if (notificationType === 'company_group_service_status') {
      const createdByField = findFieldByCode('created_by');
      const createdBy = createdByField?.Value;
      fullTitle = createdBy ? `(${createdBy}): ${subjectValue}` : subjectValue;
    } else {
      fullTitle = (ticketNumberValue ? ticketNumberValue + ': ' : '') + subjectValue;
    }

    const isTitleTruncated = subjectField?.MaxLength && fullTitle.length > subjectField.MaxLength;
    const displayTitle = this.truncateText(fullTitle, subjectField?.MaxLength);

    const fullMessage = bodyField?.Value || instance.Description || '';
    const isMessageTruncated = fullMessage.length > this.maxItemMessageLength;
    let message = fullMessage;
    if (isMessageTruncated)
      message = fullMessage.substring(0, this.maxItemMessageLength) + '...';

    const iconClass = this.getNotificationIcon(notificationType);
    const iconBackgroundColor = this.getNotificationIconColor(notificationType);

    const notification: ReceivedNotification = {
      id: id,
      title: displayTitle,
      fullTitle: fullTitle,
      message: message,
      fullMessage: fullMessage,
      isTitleTruncated: isTitleTruncated,
      isMessageTruncated: isMessageTruncated,
      timeAgo: this.calculateTimeAgo(createdDate),
      isRead: instance.Read || false,
      createdByFullName: instance?.CreatedByFullName ?? null,
      date: createdDate,
      formattedDate: this.formatShortDateUS(createdDate),
      formattedExpirationDate: expiresOnDate ? this.formatShortDateUS(expiresOnDate) : null,
      ticketIdValue: ticketIdValue,
      ticketNumberValue: ticketNumberValue,
      notificationInstance: instance,
      isExpanded: true,
      notificationType: notificationType,
      iconClass: iconClass,
      iconBackgroundColor: iconBackgroundColor,
      isUpdatingReadStatus: false,
      isCreatedByCurrentUser: !this.receivedNotifications
    };

    this.mapNotificationToPill(notification, notificationType);

    if (this.receivedNotifications && (notificationType === TLM_ALARM_TYPE || notificationType === TLM_REQUEST_TYPE)) {
      notification.tlmKind = notificationType === TLM_ALARM_TYPE ? 'alarm' : 'request';
      notification.locationName = findFieldByCode('location_name')?.Value || null;
    }

    return notification;
  }

  private formatDateUS(date: Date): string {
    if (!date) return '';
    const options: Intl.DateTimeFormatOptions = {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    };
    return new Date(date).toLocaleString('en-US', options);
  }

  private formatShortDateUS(date: Date): string {
    if (!date) return '';
    const options: Intl.DateTimeFormatOptions = {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    };
    return new Date(date).toLocaleString('en-US', options);
  }

  private truncateText(text: string, maxLength?: number | null): string {
    if (!text) return '';
    if (!maxLength || maxLength <= 0 || text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  }

  toggleNotification(notification: ReceivedNotification): void {
    notification.isExpanded = !notification.isExpanded;
  }

  async toggleReadStatus(notification: ReceivedNotification, event?: Event): Promise<void> {
    if (event) {
      event.stopPropagation();
    }

    if (notification.isUpdatingReadStatus) {
      return;
    }

    const notificationId = notification.notificationInstance?.PK_ID;
    if (!notificationId) {
      console.error('[ReceivedNotificationsOverview] Cannot update read status: notification ID is missing');
      return;
    }

    notification.isUpdatingReadStatus = true;
    const newReadStatus = !notification.isRead;

    try {
      const readRequest = {
        FkNotification: notificationId,
        User: Session.userCode,
        Read: newReadStatus
      };

      this.notificationClient.patchNotificationReadStatus(readRequest).then(response => {
        if (response?.result) {
          notification.isRead = newReadStatus;

          const sessionNotificationIndex = Session.userNotifications.findIndex(
            n => n.PK_ID === notificationId
          );

          if (sessionNotificationIndex > -1) {
            Session.userNotifications[sessionNotificationIndex].Read = newReadStatus;
          }

          if (this.showUnreadOnly) {
            this.applyFilters();
          }

          this.messageService.add({
            severity: 'success',
            summary: 'Status Updated',
            detail: `Notification marked as ${newReadStatus ? 'read' : 'unread'}`,
            life: 3000
          });
        } else {
          throw new Error('Failed to update read status');
        }
      });
    } catch (error) {
      console.error('[ReceivedNotificationsOverview] Error updating read status:', error);

      this.messageService.add({
        severity: 'error',
        summary: 'Update Failed',
        detail: 'Could not update notification status. Please try again.',
        life: 5000
      });
    } finally {
      notification.isUpdatingReadStatus = false;
    }
  }

  /**
   *
   * @param date Input date param is in UTC format.
   * @returns
   */
  calculateTimeAgo(date: Date): string {
    const now = new Date();
    let nowUTC = this.dateToUTCPipe.transform(now);
    const diffMs = nowUTC - (date.getTime() - date.getTimezoneOffset() * 60000);
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    else if (diffMins < 60) return `${diffMins} Min ago`;
    else if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  }

  groupNotificationsByDate(): void {
    this.groupedNotifications = {};

    this.notifications.forEach(notification => {
      const dateKey = this.formatDateHeader(notification.date);

      if (!this.groupedNotifications[dateKey]) {
        this.groupedNotifications[dateKey] = [];
      }

      this.groupedNotifications[dateKey].push(notification);
    });
  }

  formatDateHeader(date: Date): string {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const notifDate = new Date(date);

    if (this.isSameDay(today, notifDate)) {
      return `Today, ${notifDate.getDate()}${this.getOrdinalSuffix(notifDate.getDate())} ${this.getMonthName(notifDate.getMonth())}, ${notifDate.getFullYear()}`;
    }

    if (this.isSameDay(yesterday, notifDate)) {
      return `Yesterday, ${notifDate.getDate()}${this.getOrdinalSuffix(notifDate.getDate())} ${this.getMonthName(notifDate.getMonth())}, ${notifDate.getFullYear()}`;
    }

    return `${notifDate.getDate()}${this.getOrdinalSuffix(notifDate.getDate())} ${this.getMonthName(notifDate.getMonth())}, ${notifDate.getFullYear()}`;
  }

  isSameDay(date1: Date, date2: Date): boolean {
    return date1.getDate() === date2.getDate() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getFullYear() === date2.getFullYear();
  }

  getOrdinalSuffix(day: number): string {
    if (day > 3 && day < 21) return 'th';
    switch (day % 10) {
      case 1: return 'st';
      case 2: return 'nd';
      case 3: return 'rd';
      default: return 'th';
    }
  }

  getFooterFields(notification: ReceivedNotification): any[] {
    if (!notification.notificationInstance?.Fields) {
      return Array(5).fill(null);
    }

    const orderedFields: any[] = [];
    const isAnnouncement = notification.notificationType === 'announcement';
    const isSupportTicket = notification.notificationType === 'support_ticket';
    const isServiceStatus = notification.notificationType === 'company_group_service_status';

    if (isAnnouncement) {
      const expireOnField = notification.notificationInstance.Fields.find(f => f.Code === 'expire_on');
      expireOnField.displayValue = this.formatFieldValue(expireOnField);
      expireOnField.pillClass = this.getPillClass(expireOnField.Code);
      orderedFields.push(expireOnField);

      orderedFields.push(null);
      orderedFields.push(null);
      orderedFields.push(null);

      const linkField = notification.notificationInstance.Fields.find(f => f.Code === 'link');
      linkField.isLink = linkField.DataType?.trim() === 'url' && linkField.Value;
      linkField.displayValue = this.formatFieldValue(linkField);
      linkField.pillClass = this.getPillClass(linkField.Code);
      orderedFields.push(linkField);
    }
    else {
      for (const codeGroup of this.fieldDisplayOrder) {
        let fieldFound = false;

        for (const code of codeGroup) {
          const field = notification.notificationInstance.Fields.find(f => f.Code === code);
          if((isServiceStatus)){
            if(code === 'createdByFullName'){
              let field = {
              displayValue: notification.createdByFullName,
              pillClass: null,
              isLink: false,
              icon: null,
              color: null,
              isConversation: false,
              Code: 'created_by_fullname',
              Value: notification.createdByFullName
            };
            orderedFields.push(field);
            fieldFound = true;
            break;
            }
            else if(code === 'created_on'){
              let field = {
              displayValue: notification.formattedDate,
              pillClass: null,
              isLink: false,
              icon: null,
              color: null,
              isConversation: false,
              Code: 'created_on',
              Value: notification.formattedDate
            };
            orderedFields.push(field);
            fieldFound = true;
            break;
            }

          } else if (field) {
            field.isConversation = field.Code === 'conversation';
            field.isLink = field.DataType?.trim() === 'url' && field.Value;
            field.displayValue = this.formatFieldValue(field);
            field.pillClass = this.getPillClass(field.Code);

            orderedFields.push(field);
            fieldFound = true;
            break;
          }
        }

        if (!fieldFound) {
          orderedFields.push(null);
        }
      }
    }
    return orderedFields;
  }

  private getPillClass(code: string): string {
    switch (code) {
      // case 'created_on':
      //   return 'lavender';
      // case 'status':
      //   return 'mint';
      // case 'expire_on':
      //   return 'peach';
      default:
        return '';
    }
  }

  getFooterFieldsUnordered(notification: ReceivedNotification): any[] {
    if (!notification.notificationInstance?.Fields) {
      return Array(5).fill(null);
    }

    const excludedFields = ['subject', 'body', 'ticket_id'];

    const footerFields = notification.notificationInstance.Fields
      .filter(f => !excludedFields.includes(f.Code))
      .slice(0, 5);

    return [...footerFields, ...Array(5 - footerFields.length).fill(null)];
  }

  getFieldByCode(notification: ReceivedNotification, code: string): any {
    return notification.notificationInstance?.Fields?.find(f => f.Code === code);
  }

  formatFieldValue(field: any): string {
    if (!field || !field.Value) {
      return '';
    }

    if (field.DataType?.trim() === 'date') {
      const date = new Date(field.Value);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    }

    return field.Value;
  }

  openLink(url: string): void {
    if (url) {
      window.open(addHttp(url), '_blank');
    }
  }

  getMonthName(month: number): string {
    const months = ['January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'];
    return months[month];
  }

  /**
   * Pinned = the server ranked it above "other" (Priority 0 / 1). An AdminService without Priority yet
   * (rollout) → the TLM types stay pinned by themselves.
   */
  private isPinned(n: ReceivedNotification): boolean {
    const priority = n.notificationInstance?.Priority;
    return priority !== undefined && priority !== null ? priority < 2 : !!n.tlmKind;
  }

  private fieldValue(notification: ReceivedNotification, code: string): string | null {
    const value = this.getFieldByCode(notification, code)?.Value;
    return value !== undefined && value !== null && value !== '' ? String(value) : null;
  }

  /**
   * FENIKS US-10.5 AC-3 — Acknowledge: the server records it for me (by kind + ref_id), then my item is
   * discarded (mine only — GratNotifications keeps it for the other managers). Nothing left on the server
   * (already closed, standby) still drops the item.
   */
  acknowledgeAlarm(notification: ReceivedNotification): void {
    if (notification.isAcknowledging) return;
    if (!this.canAcknowledgeAlarms) {
      this.removeNotification(notification);
      return;
    }
    const customerCode = this.fieldValue(notification, 'customer_code');
    const kind = this.fieldValue(notification, 'kind');
    const refId = this.fieldValue(notification, 'ref_id');
    if (!customerCode || !kind || !refId) {
      this.removeNotification(notification);
      return;
    }
    notification.isAcknowledging = true;
    this.tlmClient.acknowledgeAlertByRef('Acknowledge alarm', +customerCode, kind, +refId).then(
      () => {
        notification.isAcknowledging = false;
        this.removeNotification(notification, true);
      },
      error => {
        notification.isAcknowledging = false;
        console.error('[ReceivedNotificationsOverview] Acknowledge failed:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Acknowledge failed',
          detail: 'Could not acknowledge the alarm. Please try again.',
          life: 5000
        });
      }
    );
  }

  /**
   * FENIKS US-10.5 (decision 2026-10-20) — X on an ALARM is an Acknowledge (it is recorded for me, so the
   * alarm also leaves my Today); X on a request only discards my item, as on any other notification.
   */
  removeTlmItem(notification: ReceivedNotification): void {
    if (notification.tlmKind === 'alarm' && this.canAcknowledgeAlarms) this.acknowledgeAlarm(notification);
    else this.removeNotification(notification);
  }

  /**
   * Acknowledge is TLMAlert/*, which needs "uClock Approvals". Without it (e.g. Owner after db/S9/02, who
   * still holds alarms received before) the alarm has no Acknowledge and X only discards my item.
   */
  get canAcknowledgeAlarms(): boolean {
    return TlmPermissionHelper.has(PermissionsCatalogueEnum.TlmApprovals);
  }

  /** FENIKS US-10.5 AC-7 — every open alarm of mine at all my locations (per company group); requests stay. */
  async acknowledgeAllAlarms(): Promise<void> {
    if (this.isAcknowledgingAll || this.tlmAlarms.length === 0) return;
    this.isAcknowledgingAll = true;
    const alarms = [...this.tlmAlarms];
    const groups = Array.from(new Set(alarms.map(n => this.fieldValue(n, 'company_group_code')).filter(g => !!g)));
    try {
      for (const group of groups) {
        await this.tlmClient.acknowledgeAllAlertsForGroup('Acknowledge all alarms', +group);
      }
      // Items without a group (should not happen) go one by one.
      for (const n of alarms.filter(a => !this.fieldValue(a, 'company_group_code'))) {
        const customerCode = this.fieldValue(n, 'customer_code');
        const kind = this.fieldValue(n, 'kind');
        const refId = this.fieldValue(n, 'ref_id');
        if (customerCode && kind && refId)
          await this.tlmClient.acknowledgeAlertByRef('Acknowledge alarm', +customerCode, kind, +refId);
      }
      for (const n of alarms) this.removeNotification(n, true);
      this.messageService.add({
        severity: 'success',
        summary: 'Alarms acknowledged',
        detail: `${alarms.length} alarm${alarms.length > 1 ? 's' : ''} removed from your list`,
        life: 3000
      });
    } catch (error) {
      console.error('[ReceivedNotificationsOverview] Acknowledge all failed:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Acknowledge failed',
        detail: 'Could not acknowledge the alarms. Please try again.',
        life: 5000
      });
    } finally {
      this.isAcknowledgingAll = false;
    }
  }

  markAsRead(notification: ReceivedNotification): void {
    notification.isRead = true;
  }

  removeNotification(notification: ReceivedNotification, quiet: boolean = false): void {
    const notificationId = notification.notificationInstance?.PK_ID;

    if (!notificationId) {
      console.error('[ReceivedNotificationsOverview] Cannot discard notification: notification ID is missing');
      return;
    }

    const discardRequest = {
      FkNotification: notificationId,
      User: Session.userCode,
      Discarded: true
    };

    this.notificationClient.patchNotificationDiscardedStatus(discardRequest).then(
      response => {
        if (response?.result) {
          this.allNotifications = this.allNotifications.filter(n => n.id !== notification.id);
          this.notifications = this.notifications.filter(n => n.id !== notification.id);
          this.pinnedNotifications = this.pinnedNotifications.filter(n => n.id !== notification.id);
          this.tlmAlarms = this.tlmAlarms.filter(n => n.id !== notification.id);

          this.groupNotificationsByDate();
          this.groupedNotificationKeys = Object.keys(this.groupedNotifications);

          const sessionIndex = Session.userNotifications.findIndex(n => n.PK_ID === notificationId);
          if (sessionIndex > -1) {
            Session.userNotifications.splice(sessionIndex, 1);
            Session.notificationsUpdated$?.next();   // the counter on the icon follows
          }

          if (!quiet) {
            this.messageService.add({
              severity: 'success',
              summary: 'Notification Removed',
              detail: 'Notification has been discarded successfully',
              life: 3000
            });
          }
        } else {
          throw new Error('Failed to discard notification');
        }
      },
      error => {
        console.error('[ReceivedNotificationsOverview] Error discarding notification:', error);

        this.messageService.add({
          severity: 'error',
          summary: 'Remove Failed',
          detail: 'Could not remove notification. Please try again.',
          life: 5000
        });
      }
    );
  }

  deleteNotification(notification: ReceivedNotification): void {
    this.notificationStatusChanged({ notification, newNotificationStatus: NotificationStatuses.Deleted });
  }

  notificationStatusChanged(data: { notification: ReceivedNotification, newNotificationStatus: NotificationStatuses }) {
    const notificationId = data.notification.notificationInstance?.PK_ID;

    if (!notificationId) {
      console.error('[ReceivedNotificationsOverview] Cannot discard notification: notification ID is missing');
      return;
    }

    this.notificationClient
      .changeNotificationStatus(notificationId, data.newNotificationStatus)
      .then(
        response => {
          if (data.newNotificationStatus >= NotificationStatuses.Expired) {
            this.allNotifications = this.allNotifications.filter(n => n.id !== data.notification.id);
            this.notifications = this.notifications.filter(n => n.id !== data.notification.id);

            this.groupNotificationsByDate();
            this.groupedNotificationKeys = Object.keys(this.groupedNotifications);

            // let sessionIndex = Session.userNotifications.findIndex(n => n.PK_ID === notificationId);
            // if (sessionIndex > -1) {
            //   Session.userNotifications.splice(sessionIndex, 1);
            // }

            let sessionIndex = Session.userCreatedNotifications.findIndex(n => n.PK_ID === notificationId);
            if (sessionIndex > -1) {
              Session.userCreatedNotifications.splice(sessionIndex, 1);
            }
          }
          else {
            // this.allNotifications = this.allNotifications.map((n: ReceivedNotification) => {
            //   if (n.id === data.notification.id) {
            //     n.notificationInstance.NotificationStatusId = NotificationStatuses.Published;
            //     n.notificationInstance.NotificationStatusDescription = 'Published';
            //     return n;
            //   }
            //   return n;
            // });

            // this.notifications = this.notifications.map((n: ReceivedNotification) => {
            //   if (n.id === data.notification.id) {
            //     n.notificationInstance.NotificationStatusId = NotificationStatuses.Published;
            //     n.notificationInstance.NotificationStatusDescription = 'Published';
            //     return n;
            //   }
            //   return n;
            // });

            this.groupNotificationsByDate();
            this.groupedNotificationKeys = Object.keys(this.groupedNotifications);
          }

          this.messageService.add({
            severity: 'success',
            summary: 'Notification status updated',
            detail: 'Notification status has been updated successfully',
            life: 3000
          });
        },
        error => {
          console.error('[ReceivedNotificationsOverview] Error updating notification status:', error);

          this.messageService.add({
            severity: 'error',
            summary: 'Update notification failed',
            detail: 'Could not update notification status. Please try again.',
            life: 5000
          });
        }
      );
  }

  onToogleAddNotification(closeDialog?: boolean) {
    if (closeDialog)
      this.showAddNotificationDialog = false;
    else
      this.showAddNotificationDialog = !this.showAddNotificationDialog;
  }

  onNotificationCreated(notification: NotificationInstance) {
    this.showAddNotificationDialog = false;
    Session.userCreatedNotifications.unshift(notification as any);
    this.loadNotificationsFromSession();
  }

  private getNotificationIcon(notificationType: string): string {
    const iconMap: { [key: string]: string } = {
      'announcement': 'pi-megaphone',
      'support_ticket': 'pi-ticket',
      'company_group_service_status': 'pi-cog',
      'billing_info_warning': 'pi-exclamation-triangle',
      'billing_info_deactivation': 'pi-dollar'
    };

    return iconMap[notificationType] || 'pi-bell';
  }

  private getNotificationIconColor(notificationType: string): string {
    const colorMap: { [key: string]: string } = {
      'announcement': '#dcf0ff',
      'support_ticket': '#ffefdd',
      'company_group_service_status': '#ff9999',
      'billing_info_warning': '#ffeb3b',
      'billing_info_deactivation': '#e53935'
    };

    return colorMap[notificationType] || '#ADD8E6';
  }
  private mapNotificationToPill(notification: any, notificationType: string): void {
    const pillMap: { [key: string]: string } = {
      'announcement': 'pill--peach',
      'support_ticket': 'pill--sky',
      'company_group_service_status': 'pill--lavender',
      'billing_info_warning': 'pill--pink',
      'billing_info_deactivation': 'pill--aqua'
    };

    const defaultPill = 'pill--powder';
    (notification as any).pillClass = pillMap[notificationType] || defaultPill;
    // optional short label (useful if you want small text next to title)
    (notification as any).typeLabel = notification.notificationInstance?.ShortName || null;
  }
}


function addHttp(url) {
  if (!/^(f|ht)tps?:\/\//i.test(url)) {
    url = "http://" + url;
  }
  return url;
}
